import React, { memo, useCallback, useState } from "react";

import qs from "query-string";
import { formatRoute } from "react-router-named-routes";

import Tabs from "app/components/Tabs/Tabs";
import ErrorFallbackUI from "app/components/ErrorFallbackUI";
import ReaderFirmwareTabPane from "app/screens/ReaderFirmwareScreen/components/ReaderFirmwareTabPane";

import { TabParams } from "app/types/routerType";
import { RouteComponentProps } from "react-router-dom";
import { PRIVATE_ROUTES } from "app/routers/Router.config";
import { useTranslation } from "react-i18next";
import { FirmwareDTO, FirmwareType } from "@generated/v2";
import { PaginationConfig } from "antd/lib/table";
import { useInstanceValue } from "app/hooks/useInstanceValue";
import { TableProps } from "antd/es/table";
import { ModalConfigType } from "app/types/modalType";
import { getReaderTypeLabel } from "app/helpers/deviceHelper";

enum TabOptions {
  DESKTOP_READER = "desktop-reader",
  PORTABLE_READER = "portable-reader-connect",
  TRUCK_READER = "truck-reader",
  FIVE_PORT_READER = "five-port-reader-connect",
}

interface IProps extends RouteComponentProps<TabParams> {}

function ReaderFirmwareScreen({
  history,
  match,
  location,
}: IProps): React.ReactElement {
  const { t } = useTranslation();
  const [activeTab, setActiveTab] = useState<TabOptions>(
    TabOptions.DESKTOP_READER
  );
  const [tabModal, setTabModal] = useState<ModalConfigType>({
    show: false,
  });

  const historyRef = useInstanceValue(history);
  const locationRef = useInstanceValue(location);
  const matchRef = useInstanceValue(match);

  /** pagination, order */
  const handleTableChange: TableProps<FirmwareDTO>["onChange"] = useCallback(
    (pagination: PaginationConfig) => {
      // Keep the old search params
      const currentSearch = qs.parse(locationRef.current.search);

      // and overwrite or add new values
      const values = {
        ...currentSearch,
        page: pagination.current,
      };

      historyRef.current.push({
        pathname: locationRef.current.pathname,
        search: qs.stringify(values, { arrayFormat: "comma" }),
      });
    },
    [historyRef, locationRef]
  );

  /**
   * Used for triggering the modal of Test Items and Substances
   * @param path The path to redirect to
   * @param id The id of item to be editted
   */
  const redirectTo = useCallback(
    (path: string, id?: number) => {
      const parameters = {
        tab: matchRef.current.params.tab,
        itemId: id && id,
      };

      const newRoute = formatRoute(path, parameters);
      historyRef.current.push({
        pathname: newRoute,
        search: locationRef.current.search,
      });
    },
    [historyRef, locationRef, matchRef]
  );

  return (
    <ErrorFallbackUI>
      <Tabs
        tabOptions={TabOptions}
        basePath={PRIVATE_ROUTES.READER_FIRMWARE_SCREEN.path}
        createPath={PRIVATE_ROUTES.READER_FIRMWARE_CREATE_SCREEN.path}
        hasModal={true}
        setModal={setTabModal}
        returnActiveTab={setActiveTab}
      >
        <ReaderFirmwareTabPane
          activeTab={activeTab === TabOptions.DESKTOP_READER}
          key={TabOptions.DESKTOP_READER}
          tab={t("readerFirmware.desktopReader")}
          firmwareType={FirmwareType.DesktopReader}
          handleTableChange={handleTableChange}
          redirect={redirectTo}
          modalConfig={
            tabModal.modal === TabOptions.DESKTOP_READER ? tabModal : undefined
          }
        />
        <ReaderFirmwareTabPane
          activeTab={activeTab === TabOptions.PORTABLE_READER}
          key={TabOptions.PORTABLE_READER}
          tab={t("readerFirmware.portableReaderConnect")}
          firmwareType={FirmwareType.PortableReaderConnect}
          handleTableChange={handleTableChange}
          redirect={redirectTo}
          modalConfig={
            tabModal.modal === TabOptions.PORTABLE_READER ? tabModal : undefined
          }
        />
        <ReaderFirmwareTabPane
          activeTab={activeTab === TabOptions.TRUCK_READER}
          key={TabOptions.TRUCK_READER}
          tab={t("readerFirmware.truckReader")}
          firmwareType={FirmwareType.TruckReader}
          handleTableChange={handleTableChange}
          redirect={redirectTo}
          modalConfig={
            tabModal.modal === TabOptions.TRUCK_READER ? tabModal : undefined
          }
        />
        <ReaderFirmwareTabPane
          activeTab={activeTab === TabOptions.FIVE_PORT_READER}
          key={TabOptions.FIVE_PORT_READER}
          tab={getReaderTypeLabel(FirmwareType.FivePortReaderConnect)}
          firmwareType={FirmwareType.FivePortReaderConnect}
          handleTableChange={handleTableChange}
          redirect={redirectTo}
          modalConfig={
            tabModal.modal === TabOptions.FIVE_PORT_READER
              ? tabModal
              : undefined
          }
        />
      </Tabs>
    </ErrorFallbackUI>
  );
}

export default memo(ReaderFirmwareScreen);
